import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Input,
  IconButton,
  useToast,
  Badge,
  Box,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Textarea,
} from "@chakra-ui/react";
import { CopyIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { sendAssessmentInfoEmail } from "../pages/api/recruiter";

const PublicLinkModal = ({
  isOpen,
  onClose,
  assessmentId,
  assessmentName,
  companyId,
  recruiterId,
}) => {
  const [copied, setCopied] = useState(false);
  const [publicUrl, setPublicUrl] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const toast = useToast();

  // Only render on client side to prevent SSR issues with Chakra UI Modal
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Generate the public URL when the modal opens or assessment changes
  useEffect(() => {
    if (assessmentId && typeof window !== "undefined") {
      setPublicUrl(`${window.location.origin}/public-assessment/${assessmentId}`);
    } else {
      setPublicUrl("");
    }
  }, [assessmentId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEmailInput("");
      setCopied(false);
      setIsSending(false);
    }
  }, [isOpen]);

  const handleCopyLink = async () => {
    if (!publicUrl || publicUrl.trim() === "") {
      toast({
        title: "Link not available",
        description: "Public URL is not generated yet. Please wait a moment.",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast({
        title: "Link copied to clipboard!",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      
      // Reset copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
      // Fallback for older browsers
      try {
        const textArea = document.createElement("textarea");
        textArea.value = publicUrl;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        setCopied(true);
        toast({
          title: "Link copied to clipboard!",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        toast({
          title: "Failed to copy link",
          description: "Please copy the link manually from the input field.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const handleOpenLink = () => {
    if (!publicUrl || publicUrl.trim() === "") {
      toast({
        title: "Link not available",
        description: "Public URL is not generated yet. Please wait a moment.",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    window.open(publicUrl, "_blank");
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const parseEmails = () => {
    const emails = emailInput
      .split(/[\s,;]+/)
      .map((email) => email.trim())
      .filter((email) => email.length > 0);
    
    // Validate emails and return only valid ones
    const validEmails = emails.filter((email) => isValidEmail(email));
    const invalidEmails = emails.filter((email) => !isValidEmail(email));
    
    if (invalidEmails.length > 0) {
      toast({
        title: "Invalid email addresses",
        description: `Please check: ${invalidEmails.join(", ")}`,
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }
    
    return validEmails;
  };

  const handleSendInvites = async () => {
    // Validate required props first
    if (!companyId || !recruiterId || !assessmentId) {
      toast({
        title: "Missing required information",
        description: "Company ID, Recruiter ID, and Assessment ID are required to send invites.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!publicUrl || publicUrl.trim() === "") {
      toast({
        title: "Public URL not available",
        description: "Public URL is not generated. Please wait a moment and try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const candidateEmails = parseEmails();

    if (!candidateEmails.length) {
      toast({
        title: "No valid emails",
        description: "Please add at least one valid email address.",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setIsSending(true);
    try {
      const response = await sendAssessmentInfoEmail({
        candidateEmails,
        companyId,
        recruiterId,
        testId: assessmentId,
        publicUrl,
      });

      const successCount = response?.successful?.length || 0;

      const failedCount = candidateEmails.length - successCount;
      const message = failedCount > 0
        ? `Sent ${successCount} invite${successCount > 1 ? "s" : ""}, ${failedCount} failed`
        : `Successfully sent ${successCount} invite${successCount > 1 ? "s" : ""}`;

      toast({
        title: successCount > 0 ? "Invites sent!" : "No invites sent",
        description: response?.message || message,
        status: successCount > 0 ? "success" : "warning",
        duration: 3000,
        isClosable: true,
      });
      setEmailInput("");
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send public invites";
      toast({
        title: "Public invite failed",
        description: errorMessage,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSending(false);
    }
  };

  // Don't render Modal during SSR - this prevents the addEventListener error
  // The error comes from Chakra UI's Modal trying to set up event listeners on the server
  if (!isClient) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Text>Public Assessment Link</Text>
            <Badge colorScheme="green" size="sm">
              Public
            </Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Alert status="info">
              <AlertIcon />
              <Box>
                <AlertTitle>Public Assessment Available!</AlertTitle>
                <AlertDescription>
                  This assessment is publicly accessible. Share the link below with anyone.
                </AlertDescription>
              </Box>
            </Alert>

            <Box>
              <Text fontWeight="medium" mb={2}>
                Assessment: {assessmentName}
              </Text>
              <Text fontSize="sm" color="gray.600" mb={3}>
                Public Link:
              </Text>
              <HStack spacing={2}>
                <Input
                  value={publicUrl}
                  isReadOnly
                  bg="gray.50"
                  fontSize="sm"
                />
                <IconButton
                  icon={<CopyIcon />}
                  onClick={handleCopyLink}
                  colorScheme={copied ? "green" : "blue"}
                  variant={copied ? "solid" : "outline"}
                  size="sm"
                  aria-label="Copy link"
                />
                <IconButton
                  icon={<ExternalLinkIcon />}
                  onClick={handleOpenLink}
                  colorScheme="blue"
                  variant="outline"
                  size="sm"
                  aria-label="Open link"
                />
              </HStack>
            </Box>

            <Divider />

            <Box>
              <Text fontWeight="medium" mb={2}>
                How to use this link:
              </Text>
              <VStack spacing={2} align="start">
                <Text fontSize="sm" color="gray.600">
                  1. Copy the link above
                </Text>
                <Text fontSize="sm" color="gray.600">
                  2. Share it with candidates via email, message, or any platform
                </Text>
                <Text fontSize="sm" color="gray.600">
                  3. Candidates can access the assessment without login
                </Text>
                <Text fontSize="sm" color="gray.600">
                  4. You can track their progress from your dashboard
                </Text>
              </VStack>
            </Box>

            <Divider />

            <Box>
              <Text fontWeight="medium" mb={2}>
                Send public invite emails
              </Text>
              <Text fontSize="sm" color="gray.600" mb={2}>
                Enter one or multiple candidate emails (comma or new-line separated).
              </Text>
              <Textarea
                placeholder="candidate1@example.com, candidate2@example.com"
                value={emailInput}
                onChange={(event) => setEmailInput(event.target.value)}
                rows={3}
                bg="gray.50"
              />
              <HStack justify="flex-end" mt={3}>
                <Button
                  colorScheme="green"
                  onClick={handleSendInvites}
                  isLoading={isSending}
                  isDisabled={!publicUrl || !assessmentId || !companyId || !recruiterId || isSending}
                >
                  Send Invite Email
                </Button>
              </HStack>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={2}>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleOpenLink}
              leftIcon={<ExternalLinkIcon />}
              isDisabled={!publicUrl || !assessmentId}
            >
              Open Assessment
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PublicLinkModal;

